// Angular imports
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// 3rd party imports
import { Subscription } from 'rxjs';
import * as moment from 'moment-timezone';

// CC-Common imports
import { IconConstants, Utils } from 'cc-common';

// component imports
import { ManageVendorAccountsComponent } from '../../../vendor/components/manage-vendor-accounts/manage-vendor-accounts.component';

// Service imports
import { CommunityService } from '../../../_services/data-services/community.service';
import { ContextManagerService } from '../../../_services/data-services/context-manager.service';
import { ServerErrorService } from '../../../_services/shared-services/server-error.service';
import { WorkflowVendorMissingBillsService } from '../../_services/workflow-vendor-missing-bills.service';
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';
import { HelperService } from '../../../_services/shared-services/helper.service';

// Model imports
import { CommunitySelectorResultDto } from '../../../_models/community-selector-result-dto';
import { VendorMissingBillsParameterDto } from '../../_models/workflow-vendor-missing-bills/vendor-missing-bills-parameter-dto';
import { VendorMissingBillsResultDto } from '../../_models/workflow-vendor-missing-bills/vendor-missing-bills-result-dto';
import { PropertyCommunityInfoResultDto } from '../../../_models/property-community-info-result-dto';
import { VendorAccountResultDto } from '../../../vendor/_models/vendor-account-result-dto';
import { ActionMenuEvent, ActionMenuInfo } from '../../../shared-module/_models/action-menu-info';

// Constant imports
import { StringFormatConstants } from '../../../_constants/string-format-constants';
import { CiraHelpKeyConstants } from '../../../_constants/cira-help-key-constants';
import { ActionConstants } from '../../../_constants/action-constants';

@Component({
    selector: 'app-workflow-vendor-missing-bills',
    templateUrl: './workflow-vendor-missing-bills.component.html',
    styleUrls: ['./workflow-vendor-missing-bills.component.scss']
})
export class WorkflowVendorMissingBillsComponent implements OnInit, OnDestroy {
    @ViewChild('manageVendorAccount', { static: false }) manageVendorAccount: ManageVendorAccountsComponent;

    communityList: CommunitySelectorResultDto[] = [];
    communityColumns: any[];
    communityGridBoxValue: number[];

    vendorMissingBills: VendorMissingBillsResultDto[] = [];
    selectedVendorMissingBill: VendorMissingBillsResultDto;
    selectedContext: PropertyCommunityInfoResultDto;

    communityId: number;
    modesOptions: any[] = [];
    selectedMode: string;
    isResolved: boolean;
    missingInvoiceFromDate: Date;
    missingInvoiceToDate: Date;
    actionMenuItems: Array<ActionMenuInfo> = [];

    showUpdateVendorMissingBill = false;
    showVendorMissingBillHistory = false;
    showVendorAccountNotes = false;
    loadingVisible = false;

    contextManagerSubscription: Subscription;

    dateFormat = StringFormatConstants.dateFormat;
    iconCancel: string = IconConstants.CANCEL;
    iconCheck: string = IconConstants.CHECK;
    iconCross: string = IconConstants.CROSS_MARK;
    iconRefresh: string = IconConstants.REFRESH;
    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconHistory: string = IconConstants.HISTORY;
    iconRestore = IconConstants.RESTORE;
    iconEdit = IconConstants.EDIT_NEW;
    iconView = IconConstants.VIEW;

    helpLink: string;

    vendorMissingBillsParams: VendorMissingBillsParameterDto;

    showManageVendorAccountPopup = false;
    showAddEditVendorAccount = false;
    selectedVendorAccountForEditor: VendorAccountResultDto;
    addEditVendorAccountPopUpTitle: string;
    vendorAccountConfigMode: string;

    constructor(
        private readonly contextManagerService: ContextManagerService,
        private readonly communityService: CommunityService,
        private readonly errorService: ServerErrorService,
        private readonly workflowVendorMissingBillsService: WorkflowVendorMissingBillsService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly route: ActivatedRoute,
        private readonly helperService: HelperService
    ) {
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.cbAPWFVendorMissingBills);
        this.communityColumns = this.communityService.getCommunitySelectorColumns();

        this.route.queryParamMap.subscribe((qp) => {
            const uuid = qp.get('uuid');
            if (!!uuid) {
                const filters = localStorage.getItem(uuid);
                if (!!filters) {
                    const params = JSON.parse(filters);
                    this.communityId = params.communityId;
                    this.isResolved = params.isResolved;
                    this.missingInvoiceFromDate = params.fromDate ? new Date(params.fromDate) : null;
                    this.missingInvoiceToDate = params.toDate ? new Date(params.toDate) : null;
                    this.getVendorMissingBills();
                    localStorage.removeItem(uuid);
                }
                window.history.replaceState({}, document.title, window.location.href.split('?')[0]);
            }
        });
    }

    ngOnInit() {
        this.vendorMissingBillsParams = new VendorMissingBillsParameterDto();
        this.configureFilterOptions();
        this.getActionOptions();

        this.getSelectedContext();
        this.contextManagerSubscription = this.contextManagerService.getContext().subscribe(() => {
            this.getSelectedContext();
        });
    }

    ngOnDestroy() {
        this.contextManagerSubscription.unsubscribe();
    }

    onRefreshClick(): void {
        this.getVendorMissingBills();
    }

    onActionClick(event: ActionMenuEvent<VendorMissingBillsResultDto>): void {
        this.selectedVendorMissingBill = event.record;
        if (event.action === ActionConstants.UPDATE) {
            this.onUpdateClick();
        } else if (event.action === ActionConstants.CONFIGURATION) {
            this.onVendorAccountConfigurationClick();
        }
    }

    onCommunitySelection(selectedObject): void {
        if (selectedObject && selectedObject.selectedRow.length > 0) {
            const selectedRows = selectedObject.selectedRow;
            this.communityId = selectedRows[0].communityId;
        } else {
            this.communityId = null;
        }
        this.getVendorMissingBills();
    }

    onModeOptionValueChange(): void {
        this.isResolved = null;
        if (this.selectedMode === 'Resolved') {
            this.isResolved = true;
        } else if (this.selectedMode === 'UnResolved') {
            this.isResolved = false;
        }
    }

    onUpdateMissingBillClose(event): void {
        this.showUpdateVendorMissingBill = false;

        if (event) {
            this.getVendorMissingBills();
        }
    }

    onHistoryClick(record: VendorMissingBillsResultDto): void {
        this.selectedVendorMissingBill = record;
        this.showVendorMissingBillHistory = true;
    }

    onHistoryClose(): void {
        this.showVendorMissingBillHistory = false;
    }

    onNotesClick(record: VendorMissingBillsResultDto): void {
        this.selectedVendorMissingBill = record;
        this.showVendorAccountNotes = true;
    }

    onVendorAccountNotesClose(event): void {
        this.showVendorAccountNotes = false;
        if (event) {
            this.getVendorMissingBills();
        }
    }

    openPageInWindow(): void {
        const pageUrl = window.location.href.split('?')[0];
        const uuid = Utils.getUUID();
        localStorage.setItem(uuid, JSON.stringify(this.vendorMissingBillsParams));
        window.open(`${pageUrl}?uuid=${uuid}`, '_blank').focus();
    }

    onNavigateToCommunity(): void {
        if (!!this.communityId) {
            this.helperService.openDefaultCommunityPageInNewWindow(this.communityId);
        }
    }

    onManageVendorAccountClose(): void {
        this.showManageVendorAccountPopup = false;
        this.getVendorMissingBills();
    }

    onVendorAccountEditorClose(event): void {
        this.showAddEditVendorAccount = false;
        this.showManageVendorAccountPopup = true;

        if (event) {
            if (!!this.selectedVendorAccountForEditor) {
                setTimeout(() => this.manageVendorAccount.refreshVendorAccountNotes(), 250);
                setTimeout(() => this.manageVendorAccount.refreshVendorAccountHistory(), 250);
            }

            setTimeout(() => this.manageVendorAccount.reloadManageAccounts(), 250);
        }
    }

    onVendorAccountSelectionForEditor(event): void {
        this.selectedVendorAccountForEditor = event;
        if (!!this.selectedVendorAccountForEditor) {
            this.addEditVendorAccountPopUpTitle = 'Edit Vendor Account';
            this.vendorAccountConfigMode = 'edit';
        } else {
            this.addEditVendorAccountPopUpTitle = 'Add Vendor Account';
            this.vendorAccountConfigMode = 'add';
        }
        this.showAddEditVendorAccount = true;
        this.showManageVendorAccountPopup = false;
    }

    onVendorNameClick(data: VendorMissingBillsResultDto): void {
        if (Utils.notNullAndDefined(data)) {
            const contextType = 'Community';
            const contextId = data.communityId;
            const vendorCode = data.vendorCode;
            const queryString = `contextType=${encodeURIComponent(
                contextType
            )}&contextId=${contextId}&vendorCode=${encodeURIComponent(vendorCode)}`;
            this.helperService.openDefaultVendorManagementPageInNewWindow(queryString);
        }
    }

    private onUpdateClick(): void {
        this.showUpdateVendorMissingBill = true;
    }

    private onVendorAccountConfigurationClick(): void {
        this.showManageVendorAccountPopup = true;
    }

    private getVendorMissingBills(): void {
        const baseParams: VendorMissingBillsParameterDto = new VendorMissingBillsParameterDto();
        baseParams.isResolved = this.isResolved;
        if (Utils.isTrue(this.isResolved) || Utils.isNullOrUndefined(this.isResolved)) {
            baseParams.fromDate = this.missingInvoiceFromDate;
            baseParams.toDate = this.missingInvoiceToDate;
        }
        if (!!this.communityId) {
            baseParams.communityId = this.communityId;
        }
        this.vendorMissingBillsParams = baseParams;

        this.loadingVisible = true;
        this.workflowVendorMissingBillsService.getVendorMissingBills(this.vendorMissingBillsParams).subscribe(
            (result: VendorMissingBillsResultDto[]) => {
                this.vendorMissingBills = result;
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getCommunityList() {
        this.communityService.getCommunities().subscribe(
            (data) => {
                this.communityList = data;
                this.communityGridBoxValue = this.communityId ? [this.communityId] : null;
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private configureFilterOptions(): void {
        this.modesOptions = [
            {
                displayText: 'All',
                value: 'All'
            },
            {
                displayText: 'UnResolved',
                value: 'UnResolved'
            },
            { displayText: 'Resolved', value: 'Resolved' }
        ];
        this.isResolved = !!this.isResolved ? this.isResolved : null;

        this.missingInvoiceToDate = !!this.missingInvoiceToDate ? this.missingInvoiceToDate : moment().toDate();
        this.missingInvoiceFromDate = !!this.missingInvoiceFromDate
            ? this.missingInvoiceFromDate
            : moment(this.missingInvoiceToDate).subtract(6, 'months').toDate();

        this.selectedMode = !!this.isResolved ? 'Resolved' : 'UnResolved';
        if (this.selectedMode === 'Resolved') {
            this.isResolved = true;
        } else if (this.selectedMode === 'UnResolved') {
            this.isResolved = false;
        }
    }

    private getActionOptions(): void {
        this.actionMenuItems = [
            {
                action: ActionConstants.UPDATE,
                icon: this.iconEdit,
                name: 'Update Status'
            },
            {
                action: ActionConstants.CONFIGURATION,
                icon: this.iconView,
                name: 'View Vendor Account Configuration'
            }
        ];
    }

    private getSelectedContext(): void {
        this.selectedContext = this.contextManagerService.getSelectedContext();
        if (!!this.selectedContext && this.selectedContext.communityId) {
            this.communityId = this.selectedContext.communityId;
        } else {
            this.getCommunityList();
        }
        this.getVendorMissingBills();
    }
}
